import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import {
  CalendarDays,
  Clock,
  MapPin,
  Loader2,
  ArrowRight,
  Footprints,
  Flame,
  Volleyball,
  Users,
  Sparkles,
  Coffee,
  ShieldCheck,
  MessageCircle,
} from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { notifyForm } from "@/lib/notifyForm";
import heroImage from "@/assets/run-with-healic-hero.jpg.asset.json";


const PAY_LINK = "https://rzp.io/rzp/wyU7sBqs";
const WHATSAPP_COMMUNITY =
  "https://chat.whatsapp.com/BX1bVCJBkpV3uSUHqFMGIc?s=cl&p=a&mlu=4";

export const Route = createFileRoute("/run-with-healic")({
  head: () => ({
    meta: [
      { title: "Run with Healic — Community Run Registration | Agra" },
      {
        name: "description",
        content:
          "Join Run with Healic, a 4 KM community run in Agra on 30th August 2026, 5:30 AM. Warm-up, post-run games and refreshments. Register for ₹99 — limited slots.",
      },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "Run with Healic — Community Run in Agra" },
      {
        property: "og:description",
        content:
          "4 KM community run, fun fitness warm-up, games and refreshments. Sunday 30th August 2026, 5:30 AM in Agra. Register for ₹99.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://healic.in/run-with-healic" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Run with Healic — Community Run in Agra" },
      {
        name: "twitter:description",
        content:
          "Lace up for a 4 KM Sunday morning community run with Healic Multispeciality Hospital. Limited slots at ₹99.",
      },
    ],
    links: [{ rel: "canonical", href: "https://healic.in/run-with-healic" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SportsEvent",
          name: "Run with Healic — Community Run",
          startDate: "2026-08-30T05:30:00+05:30",
          eventStatus: "https://schema.org/EventScheduled",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          location: {
            "@type": "Place",
            name: "Agra (venue to be announced)",
            address: { "@type": "PostalAddress", addressLocality: "Agra", addressCountry: "IN" },
          },
          organizer: {
            "@type": "MedicalOrganization",
            name: "Healic Multispeciality Hospital",
            url: "https://healic.in",
          },
          offers: {
            "@type": "Offer",
            price: "99",
            priceCurrency: "INR",
            url: "https://healic.in/run-with-healic",
            availability: "https://schema.org/LimitedAvailability",
          },
        }),
      },
    ],
  }),
  component: RunWithHealicPage,
});

const eventDetails = [
  { icon: CalendarDays, label: "Date", value: "30th August 2026 (Sunday)" },
  { icon: Clock, label: "Time", value: "5:30 AM onwards" },
  { icon: MapPin, label: "Location", value: "Agra (Venue TBA)" },
];

const expectations = [
  { icon: Footprints, label: "4 KM Community Run" },
  { icon: Flame, label: "Fun Fitness & Warm-up" },
  { icon: Volleyball, label: "Post-run Games" },
  { icon: Users, label: "Fitness Enthusiasts" },
  { icon: Sparkles, label: "Great Vibes" },
  { icon: Coffee, label: "Refreshments" },
];

const consentPoints = [
  "No separate confirmation email or message is sent. Your payment receipt is your confirmation.",
  "Please keep a screenshot of your payment receipt and show it at the venue if the team asks for it.",
  "Organisers may deny entry or remove a participant in case of misconduct, inappropriate behaviour or non-cooperation.",
];

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your full name"),
  phone: z.string().trim().regex(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number"),
  instagram: z.string().trim().max(60).optional(),
  gender: z.string().min(1, "Please select your gender"),
  age: z.coerce.number().int().min(10, "Minimum age is 10").max(100, "Please enter a valid age"),
  source: z.string().min(1, "Please tell us how you heard about us"),
  consent: z.literal(true, { message: "Please accept the terms to continue" }),
});

const inputClass =
  "w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20";

function RunWithHealicPage() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    instagram: "",
    gender: "",
    age: "",
    source: "",
    consent: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const set = (key: string, value: string | boolean) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: "" }));
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      toast.error("Please check the highlighted fields");
      return;
    }
    setSubmitting(true);
    const data = parsed.data;
    await notifyForm({
      formType: "Run with Healic — Community Run",
      source: "Run with Healic (Agra, 30 Aug 2026)",
      fields: [
        { label: "Name", value: data.name },
        { label: "Mobile", value: data.phone },
        { label: "Instagram", value: data.instagram || "—" },
        { label: "Gender", value: data.gender },
        { label: "Age", value: data.age },
        { label: "Heard about us via", value: data.source },
      ],
    });
    setSubmitting(false);
    toast.success("Registered! Redirecting you to payment — see you at the start line.");
    window.open(PAY_LINK, "_blank", "noopener");
  }

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <img
          src={heroImage.url}
          alt="Runners in Healic-branded red tees during a sunrise community run in Agra"
          fetchPriority="high"
          decoding="async"
          width={1920}
          height={1088}
          className="absolute inset-0 h-full w-full object-cover object-center opacity-40"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-primary/85 via-primary/70 to-primary"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-accent/25 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-white/10 blur-3xl"
        />
        <div className="relative mx-auto max-w-5xl px-4 pb-24 pt-14 text-center sm:pb-28 sm:pt-20">

          <span className="inline-flex items-center gap-2 rounded-pill bg-accent px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-accent-foreground">
            <Footprints className="h-4 w-4" /> Community Run
          </span>
          <h1 className="mt-5 text-3xl font-extrabold leading-tight sm:text-5xl">
            Run with Healic
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-primary-foreground/85 sm:text-base">
            On Sunday, 30th August, let&apos;s run towards a{" "}
            <span className="font-bold text-accent">healthier lifestyle.</span> Run with Healic
            comes to Agra for the very first time — 4 kilometres, great energy and a morning
            you&apos;ll remember.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="#register"
              className="inline-flex items-center gap-2 rounded-pill bg-accent px-6 py-3 text-sm font-bold text-accent-foreground shadow-cta transition-transform hover:scale-105"
            >
              Register for ₹99 <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={WHATSAPP_COMMUNITY}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-pill bg-white/10 px-6 py-3 text-sm font-semibold ring-1 ring-white/20 transition-colors hover:bg-white/20"
            >
              <MessageCircle className="h-4 w-4" /> Join WhatsApp community
            </a>
          </div>
        </div>
      </section>

      {/* Event details */}
      <section className="relative z-10 mx-auto -mt-16 max-w-5xl px-4 sm:-mt-20">
        <div className="grid gap-5 rounded-2xl border border-border bg-card p-5 shadow-card sm:grid-cols-3 sm:p-7">

          {eventDetails.map((d) => (
            <div key={d.label} className="flex items-start gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent/15 text-accent">
                <d.icon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                  {d.label}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-foreground">{d.value}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* What to expect */}
      <section className="mx-auto max-w-5xl px-4 py-12 sm:py-16">
        <h2 className="text-center text-2xl font-bold text-foreground sm:text-3xl">
          What to expect
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-muted-foreground">
          After the run we spend time together, connect with fellow runners and enjoy a fun-filled
          morning as a community.
        </p>
        <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3">
          {expectations.map((item) => (
            <div
              key={item.label}
              className="group rounded-2xl border border-border bg-card p-4 text-center shadow-soft transition-all hover:-translate-y-1 hover:shadow-card sm:p-6"
            >
              <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary transition-colors group-hover:bg-accent group-hover:text-accent-foreground">
                <item.icon className="h-5 w-5" />
              </span>
              <p className="mt-3 text-sm font-semibold text-foreground">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Fee */}
      <section className="mx-auto max-w-5xl px-4">
        <div className="rounded-2xl bg-primary px-6 py-8 text-center text-primary-foreground shadow-card">
          <p className="text-[11px] font-bold uppercase tracking-widest text-accent">
            Registration fee
          </p>
          <p className="mt-2 text-4xl font-extrabold sm:text-5xl">₹99</p>
          <p className="mt-2 text-sm text-primary-foreground/80">
            per person · limited slots available, register before they fill up
          </p>
        </div>
      </section>

      {/* Registration */}
      <section id="register" className="mx-auto max-w-3xl scroll-mt-24 px-4 py-12 sm:py-16">
        <h2 className="text-center text-2xl font-bold text-foreground sm:text-3xl">Register now</h2>
        <p className="mt-3 text-center text-sm text-muted-foreground">
          Fill the form to reserve your spot, then complete the ₹99 payment to confirm.
        </p>

        <form
          onSubmit={handleSubmit}
          className="mt-8 space-y-5 rounded-2xl border border-border bg-card p-5 shadow-card sm:p-8"
        >
          <Field label="Name" required error={errors.name}>
            <input
              className={inputClass}
              placeholder="Enter your full name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </Field>

          <Field label="Mobile number" required error={errors.phone}>
            <input
              className={inputClass}
              inputMode="numeric"
              maxLength={10}
              placeholder="10-digit mobile number"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value.replace(/\D/g, ""))}
            />
          </Field>

          <Field label="Instagram handle" error={errors.instagram}>
            <input
              className={inputClass}
              placeholder="@yourhandle"
              value={form.instagram}
              onChange={(e) => set("instagram", e.target.value)}
            />
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Gender" required error={errors.gender}>
              <select
                className={inputClass}
                value={form.gender}
                onChange={(e) => set("gender", e.target.value)}
              >
                <option value="">Select your gender</option>
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
            </Field>
            <Field label="Age" required error={errors.age}>
              <input
                className={inputClass}
                inputMode="numeric"
                placeholder="Enter your age"
                value={form.age}
                onChange={(e) => set("age", e.target.value.replace(/\D/g, "").slice(0, 3))}
              />
            </Field>
          </div>

          <Field label="How did you get to know about us?" required error={errors.source}>
            <select
              className={inputClass}
              value={form.source}
              onChange={(e) => set("source", e.target.value)}
            >
              <option value="">Select an option</option>
              <option>Instagram</option>
              <option>Facebook</option>
              <option>WhatsApp</option>
              <option>Word of mouth</option>
              <option>Other</option>
            </select>
          </Field>

          {/* Consent */}
          <div className="rounded-2xl border border-border bg-muted/40 p-5">
            <p className="flex items-center gap-2 text-sm font-bold text-foreground">
              <ShieldCheck className="h-4 w-4 text-accent" /> Important information & consent
            </p>
            <ul className="mt-3 space-y-2">
              {consentPoints.map((p) => (
                <li key={p} className="flex gap-2 text-xs leading-relaxed text-muted-foreground">
                  <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 rounded-xl bg-primary px-4 py-3 text-xs font-semibold text-primary-foreground">
              For more updates, join our{" "}
              <a
                href={WHATSAPP_COMMUNITY}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-accent"
              >
                WhatsApp community
              </a>
              .
            </p>

            <label className="mt-4 flex cursor-pointer items-start gap-3 text-sm text-foreground">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[var(--accent)]"
                checked={form.consent}
                onChange={(e) => set("consent", e.target.checked)}
              />
              <span>I have read and acknowledged the terms and conditions</span>
            </label>
            {errors.consent && (
              <p className="mt-2 text-xs font-medium text-destructive">{errors.consent}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-pill bg-accent px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-accent-foreground shadow-cta transition-transform hover:scale-[1.02] disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Let&apos;s run <ArrowRight className="h-4 w-4" />
          </button>
          <p className="text-center text-xs text-muted-foreground">
            You&apos;ll be taken to secure payment. Your spot is confirmed once payment is complete.
          </p>
          <p className="text-center text-sm font-bold text-primary">
            Your pace. Your run. Your community.
          </p>
        </form>

        <p className="mt-8 border-t border-border pt-6 text-center text-xs text-muted-foreground">
          Run with Healic · making health a community affair · @RunWithHealic
        </p>
      </section>
    </PageShell>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      {children}
      {error && <p className="mt-1.5 text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}
